import { isValidElement, ReactElement, useEffect, useState } from 'react'
import { Color } from '@mrshmllw/smores-react'

/*eslint no-undef: 0*/
/**
 * @file General helper methods (debounce, clone, throttle, etc).
 */
import easings from './easings'

import { ProductPlan } from 'types'

// Capitalize any text
const capitalize = (text: string) =>
  text ? text.charAt(0).toUpperCase() + text.toLowerCase().slice(1) : ''

export const pascalToSentenceCase = (str: string) =>
  str
    .toLowerCase()
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const pluralize = (num: number, text: string): string =>
  `${text.toLowerCase()}${text.toLowerCase().endsWith('s') || num === 1 ? '' : 's'}`

/**
 * Debounces a method.
 * @param {function} fn - The function to be debounced.
 * @param {number} delay - The debouncing delay in millisecond
 * @returns {function}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debounce = (fn: any, delay: number) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let timerId: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...args: any) => {
    if (timerId) {
      clearTimeout(timerId)
    }
    timerId = window.setTimeout(() => {
      fn(...args)
      timerId = null
    }, delay)
  }
}

/**
 * Throttles a method.
 * @param {function} fn - The function to be throttled.
 * @param {number} delay - The throttle delay.
 * @returns {function}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const throttle = (fn: any, delay: number) => {
  let lastCall = 0
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const throttledFunction = (...args: any) => {
    const now = new Date().getTime()

    if (now - lastCall < delay) {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      timeoutId = setTimeout(
        () => {
          lastCall = now
          fn(...args) // eslint-disable-line consistent-return
        },
        delay - (now - lastCall),
      )
      return
    }

    lastCall = now
    return fn(...args) // eslint-disable-line consistent-return
  }

  throttledFunction.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  return throttledFunction
}

/**
 * Formats numbers by adding commas. e.g: 1234 will become 1,234
 * @param {number} num - The number to be formatted.
 * @returns {string}
 */
const commaSeparatedNumber = (num: number) => {
  // for numbers with more than 3 digits after the decimal point
  const parts = num.toString().split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

/**
 * Scroll transition, scrolls to a given destination applying an easing.
 * @param {(number|object)} destination - The Y coordinate/DOM node where the scroll will end.
 * @param {number} [duration] - The duration of the transition.
 * @param {function} [callback] - The function to be called at the end of execution (optional).
 */
const smoothScroll = (
  element: HTMLElement,
  duration = 300,
  easing: keyof typeof easings = 'linear',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: any,
) => {
  const start = window.pageYOffset
  const current = document.documentElement.scrollTop || document.body.scrollTop

  const documentHeight = Math.max(
    document.body.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.clientHeight,
    document.documentElement.scrollHeight,
    document.documentElement.offsetHeight,
  )
  const windowHeight =
    window.innerHeight ||
    document.documentElement.clientHeight ||
    document.getElementsByTagName('body')[0].clientHeight

  const destinationOffset = typeof element === 'number' ? element : element.offsetTop

  const destinationOffsetToScroll = Math.round(
    documentHeight - destinationOffset < windowHeight
      ? documentHeight - windowHeight
      : destinationOffset,
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const animate = (innerCallback: any) => {
    const startTime = new Date().getTime()
    const animation = () => {
      let time = easings[easing]((new Date().getTime() - startTime) / duration)
      if (time <= 1) {
        requestAnimationFrame(animation)
      } else {
        time = 1
      }
      innerCallback(time, time === 1)
    }
    animation()
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  animate((timeFunction: any, done: any) => {
    window.scrollTo(0, Math.ceil(timeFunction * (destinationOffsetToScroll - start) + current))
    if (done && callback) {
      callback()
    }
  })
}

/**
 * Creates a deep clone of the passed collection data.
 * @param {(array|object)} data - The data collection that needs cloning.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const clone = (data: Record<any, any>) => JSON.parse(JSON.stringify(data))

export { capitalize, clone, commaSeparatedNumber, debounce, smoothScroll, throttle }
/**
 * Return the text formatted value of a specific Channel
 * @param {string} channel - The channel const you want to get text format for.
 */
export const getSelectedChannel = (channel: string) => {
  switch (channel) {
    case 'COMPARE_THE_MARKET':
      return 'Compare the Market'
    case 'CONFUSED':
      return 'Confused'
    case 'GO_COMPARE':
      return 'GoCompare'
    case 'MONEY_SUPERMARKET':
      return 'MoneySuperMarket'
    case 'QUOTEZONE':
      return 'Quotezone'
    case 'WEB':
      return 'our website'
  }
}

const getBreakpoint = (overrideBreakpoint = 768) => {
  return window.innerWidth < overrideBreakpoint ? 'mobile' : 'desktop'
}

export const useBreakpoint = (overrideBreakpoint?: number) => {
  const [deviceType, setDeviceType] = useState<'mobile' | 'desktop'>(
    getBreakpoint(overrideBreakpoint),
  )

  useEffect(() => {
    const handleResize = () => {
      setDeviceType(() => getBreakpoint(overrideBreakpoint))
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [overrideBreakpoint])

  return deviceType
}

export const firstCharLowerCase = (str: string) => str[0].toLowerCase() + str.slice(1)

type ConvertFirstLetterToLowerCase<T> = {
  [K in keyof T as Uncapitalize<string & K>]: T[K]
}

export const lowerCaseKeys = <T extends object>(obj: T): ConvertFirstLetterToLowerCase<T> =>
  Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [firstCharLowerCase(k), v]),
  ) as ConvertFirstLetterToLowerCase<T>

type QueryParamType = {
  queryParams: string | undefined
  redirectUrl: string
  isAddingQueryParams?: boolean
}

export const appendParamsOnQuoteRedirect = ({
  queryParams,
  redirectUrl,
  isAddingQueryParams = true,
}: QueryParamType): string => {
  if (!queryParams) {
    return redirectUrl
  }

  // If adding query params already, remove the '?' from the query params and replace with '&'
  // If we're not adding query params, append the query params to the redirect url
  return `${
    isAddingQueryParams
      ? redirectUrl + '&' + queryParams.slice(1, queryParams.length)
      : redirectUrl + queryParams
  }`
}

export const returnProductPlanColour = (productPlan: ProductPlan): Color => {
  switch (productPlan) {
    case ProductPlan.LIGHTEST:
      return 'fairyFloss'
    case ProductPlan.LIGHTEST_GO:
      return 'fairyFloss'
    case ProductPlan.ESSENTIAL:
      return 'bubblegum'
    case ProductPlan.ESSENTIAL_GO:
      return 'bubblegum'
    case ProductPlan.ORIGINAL:
      return 'marshmallowPink'
    case ProductPlan.ORIGINAL_GO:
      return 'marshmallowPink'
    case ProductPlan.PLUS:
      return 'lollipop'
    case ProductPlan.PLUS_GO:
      return 'lollipop'
    default:
      return 'marshmallowPink'
  }
}

export const isReactElement = (obj: unknown): obj is ReactElement => {
  return isValidElement(obj)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepEquals = (a: any, b: any): boolean => {
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) {
    return a === b // If they're not objects, use strict equality
  }

  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)

  // Check if both objects have the same number of keys
  if (aKeys.length !== bKeys.length) {
    return false
  }

  // Check if all keys in a exist in b
  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) {
      return false
    }
  }

  // Check if the values of each key are deeply equal
  for (const key of aKeys) {
    if (!deepEquals(a[key], b[key])) {
      return false
    }
  }

  // If all conditions pass, the objects are deeply equal
  return true
}

export const nullifyEmptyShallowValues = <T extends object>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, v === '' || v === undefined ? null : v]),
  ) as T
